import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Scrollbars from "react-custom-scrollbars-2";
import ImageWithBasePath from "../imageWithBasePath";
import "../../../style/icon/tabler-icons/webfont/tabler-icons.css";
import { useDispatch, useSelector } from "react-redux";
import { SidebarDataTest } from "../../data/json/sidebarMenu";
import usePreviousRoute from "./usePreviousRoute";
import { all_routes } from "../../../feature-module/router/all_routes";

export interface SidebarItem {
  label?: string;
  link?: string;
  submenu?: boolean;
  showSubRoute?: boolean;
  icon?: string;
  base?: string;
  base2?: string;
  base3?: string;
  materialicons?: string;
  customSubmenuTwo?: boolean;
  page1?: string;
  page2?: string;
  roles?: string[];
  notAllowedRoles?: string[];
  allowedEmployeeIds?: string[];
  departments?: string[];
  submenuItems?: SidebarItem[];
  links?: string[];
  changeLogVersion?: boolean;
  dot?: boolean;
  showAsTab?: boolean;
  separateRoute?: boolean;
  title?: string;
  permissionModuleKey?: string;
}

export interface SidebarSection {
  title?: string;
  icon?: string;
  showAsTab?: boolean;
  separateRoute?: boolean;
  submenuItems?: SidebarItem[];
}

const Sidebar = () => {
  const routes = all_routes;
  const location = useLocation();
  const dispatch = useDispatch();
  const permissions = useSelector((state: any) => state.auth.permissions);
  const role = useSelector((state: any) => state.auth.role);
  const employeeId = useSelector((state: any) => state.auth.employeeId);
  const previousLocation = usePreviousRoute();
  const [subOpen, setSubopen] = useState("Dashboard");
  const [subsidebar, setSubsidebar] = useState("");

  useEffect(() => {
    const currentMenu = localStorage.getItem("menuOpened") || "Dashboard";
    setSubopen(currentMenu);
  }, [location.pathname]);

  const toggleSidebar = (title: string) => {
    localStorage.setItem("menuOpened", title);
    setSubopen((prev) => (prev === title ? "" : title));
  };

  const toggleSubsidebar = (subitem: string) => {
    setSubsidebar((prev) => (prev === subitem ? "" : subitem));
  };
  const hasPermissionAccess = (moduleKey?: string): boolean => {
    if (!permissions || !moduleKey) return false;

    // Check for exact match first
    let modulePermissions = permissions[moduleKey];

    // If not found, try case-insensitive search
    if (!modulePermissions) {
      const key = Object.keys(permissions).find(
        (k) => k.toLowerCase() === moduleKey.toLowerCase()
      );
      modulePermissions = key ? permissions[key] : null;
    }

    // If still not found, return false
    if (!modulePermissions) {
      console.warn(`Permission not found for module: ${moduleKey}`);
      return false;
    }

    return !!(
      modulePermissions.allow_all ||
      modulePermissions.read ||
      modulePermissions.write ||
      modulePermissions.create ||
      modulePermissions.delete
    );
  };

  const hasAccess = (menu: SidebarItem): boolean => {
    // Special handling for Dashboard - always allow access regardless of permissions
    if (menu.permissionModuleKey === "Dashboard") {
      // Only check role-based access for Dashboard
      if (menu.roles && !menu.roles.includes(role)) {
        return false;
      }
      if (menu.notAllowedRoles && menu.notAllowedRoles.includes(role)) {
        return false;
      }
      // Check employee ID access if specified
      if (
        menu.allowedEmployeeIds &&
        !menu.allowedEmployeeIds.includes(employeeId)
      ) {
        return false;
      }
      return true; // Allow Dashboard access even with empty permissions
    }

    // Check role-based access
    if (menu.roles && !menu.roles.includes(role)) {
      return false;
    }
    if (menu.notAllowedRoles && menu.notAllowedRoles.includes(role)) {
      return false;
    }

    // Check employee ID access
    if (
      menu.allowedEmployeeIds &&
      !menu.allowedEmployeeIds.includes(employeeId)
    ) {
      return false;
    }

    // Check permission-based access for non-Dashboard items
    if (menu.permissionModuleKey) {
      const hasPermission = hasPermissionAccess(menu.permissionModuleKey);
      if (!hasPermission) {
        return false;
      }
    }

    // For items with submenus, check if any submenu item has access
    if (Array.isArray(menu.submenuItems) && menu.submenuItems.length > 0) {
      return menu.submenuItems.some(hasAccess);
    }

    // If there's no permission key and no other restrictions, allow access
    if (
      !menu.permissionModuleKey &&
      !menu.roles &&
      !menu.allowedEmployeeIds &&
      !menu.notAllowedRoles
    ) {
      return true;
    }

    // If there's no permission key but there are role/employee restrictions,
    // we already checked them above, so allow access
    if (!menu.permissionModuleKey) {
      return true;
    }

    // We've already handled the permission check above
    return true;
  };

  // Function to get the appropriate dashboard link based on role
  const getDashboardLink = (): string => {
    if (role === "Superadmin") {
      return routes.superAdminDashboard;
    } else if (["Admin", "Manager"].includes(role)) {
      return routes.adminDashboard;
    } else {
      return routes.employeeDashboard;
    }
  };

  const filterDashboardItems = (items: SidebarItem[]): SidebarItem[] => {
    return items.filter((item) => {
      if (item.permissionModuleKey === "Dashboard") {
        // Superadmin
        if (role === "Superadmin" && item.roles?.includes("Superadmin")) {
          return true;
        }
        // Admin/Manager
        if (
          ["Admin", "Manager"].includes(role) &&
          item.roles?.some((r) => ["Admin", "Manager"].includes(r))
        ) {
          return true;
        }
        // ✅ Fallback for ANY new role → employeeDashboard
        if (
          !["Admin", "Manager", "Superadmin"].includes(role) &&
          item.link === routes.employeeDashboard
        ) {
          return true;
        }
        return false;
      }
      return hasAccess(item);
    });
  };

  // Special function to check if a section should be visible
  const shouldShowSection = (section: SidebarSection): boolean => {
    if (section.title === "Dashboard") {
      // Always show Dashboard section
      return true;
    }
    // For other sections, check if any submenu item has access
    return (
      Array.isArray(section.submenuItems) &&
      section.submenuItems.some(hasAccess)
    );
  };

  // Function to get filtered submenu items for a section
  const getFilteredSubmenuItems = (section: SidebarSection): SidebarItem[] => {
    if (!section.submenuItems) return [];

    if (section.title === "Dashboard") {
      return filterDashboardItems(section.submenuItems);
    }

    return section.submenuItems.filter(hasAccess);
  };

  // Debug: Log permissions for troubleshooting
  useEffect(() => {
  }, [permissions, role, employeeId]);

  // Show dashboard even when permissions are empty or loading
  const showDashboardWhenEmpty = () => {
    return (
      <div className="sidebar" id="sidebar">
        <div className="sidebar-logo">
          <Link to="/" className="logo logo-normal">
            <ImageWithBasePath src="assets/img/logo.svg" alt="Logo" />
          </Link>
          <Link to="/" className="logo-small">
            <ImageWithBasePath src="assets/img/logo-small.svg" alt="Logo" />
          </Link>
          <Link to="/" className="dark-logo">
            <ImageWithBasePath src="assets/img/logo-white.svg" alt="Logo" />
          </Link>
        </div>
        <Scrollbars>
          <div className="sidebar-inner slimscroll">
            <div id="sidebar-menu" className="sidebar-menu">
              <ul>
                <li className="menu-title">
                  <span>Dashboard</span>
                </li>
                <li>
                  <ul>
                    <li className="submenu">
                      <Link
                        to={getDashboardLink()}
                        state={{ employee: employeeId }}
                        className={
                          location.pathname.includes("dashboard")
                            ? "active"
                            : ""
                        }
                      >
                        <i className="ti ti-smart-home"></i>
                        <span>Dashboard</span>
                      </Link>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </Scrollbars>
      </div>
    );
  };

  // If permissions are empty or loading, show dashboard only
  if (!permissions || Object.keys(permissions).length === 0) {
    return showDashboardWhenEmpty();
  }

  return (
    <div className="sidebar" id="sidebar">
      <div className="sidebar-logo">
        <Link to="/" className="logo logo-normal">
          <ImageWithBasePath src="assets/img/logo.svg" alt="Logo" />
        </Link>
        <Link to="/" className="logo-small">
          <ImageWithBasePath src="assets/img/logo-small.svg" alt="Logo" />
        </Link>
        <Link to="/" className="dark-logo">
          <ImageWithBasePath src="assets/img/logo-white.svg" alt="Logo" />
        </Link>
      </div>
      <Scrollbars>
        <div className="sidebar-inner slimscroll">
          <div id="sidebar-menu" className="sidebar-menu">
            <ul>
              {SidebarDataTest.map((mainLabel: SidebarSection, idx: number) => {
                // Check if section should be visible
                if (!shouldShowSection(mainLabel)) {
                  return null;
                }

                // Get filtered submenu items
                const filteredSubmenuItems = getFilteredSubmenuItems(mainLabel);

                // If no submenu items after filtering, don't show the section
                if (filteredSubmenuItems.length === 0) {
                  return null;
                }

                return (
                  <React.Fragment key={`main-${idx}`}>
                    <li className="menu-title">
                      <span>{mainLabel.title}</span>
                    </li>
                    <li>
                      <ul>
                        {filteredSubmenuItems.map(
                          (menu: SidebarItem, i: number) => {
                            // Create link array for active state checking
                            const link_array: string[] = [];
                            menu.submenuItems?.forEach((link: any) => {
                              if (link?.link) link_array.push(link.link);
                              link.submenuItems?.forEach((sublink: any) => {
                                if (sublink?.link)
                                  link_array.push(sublink.link);
                              });
                            });
                            menu.links = link_array;

                            const isActive =
                              menu.links?.includes(location.pathname) ||
                              menu.link === location.pathname;

                            // Special handling for Dashboard menu items
                            if (menu.permissionModuleKey === "Dashboard") {
                              return (
                                <li className="submenu" key={`dashboard-${i}`}>
                                  <Link
                                    to={getDashboardLink()}
                                    state={{ employee: employeeId }}
                                    className={`${
                                      location.pathname.includes("dashboard")
                                        ? "active"
                                        : ""
                                    }`}
                                  >
                                    <i
                                      className={`ti ti-${
                                        menu.icon || "smart-home"
                                      }`}
                                    ></i>
                                    <span>{menu.label || "Dashboard"}</span>
                                  </Link>
                                </li>
                              );
                            }

                            return (
                              <li className="submenu" key={`submenu-${i}`}>
                                <Link
                                  to={menu.submenu ? "#" : menu.link || "#"}
                                  state={{ employee: employeeId }}
                                  className={`${
                                    subOpen === menu.label ? "subdrop" : ""
                                  } ${isActive ? "active" : ""}`}
                                  onClick={() =>
                                    toggleSidebar(menu.label || "")
                                  }
                                >
                                  <i className={`ti ti-${menu.icon}`}></i>
                                  <span>{menu.label}</span>
                                  {menu.submenu && (
                                    <span className="menu-arrow" />
                                  )}
                                </Link>

                                {menu.submenu !== false &&
                                  subOpen === menu.label &&
                                  menu.submenuItems && (
                                    <ul style={{ display: "block" }}>
                                      {menu.submenuItems
                                        .filter(hasAccess)
                                        .map((item: SidebarItem, j: number) => (
                                          <li
                                            className={
                                              item.submenuItems
                                                ? "submenu submenu-two"
                                                : ""
                                            }
                                            key={`item-${j}`}
                                          >
                                            <Link
                                              to={
                                                item.submenu
                                                  ? "#"
                                                  : item.link || "#"
                                              }
                                              state={{ employee: employeeId }}
                                              className={`${
                                                item.submenuItems?.some(
                                                  (link: any) =>
                                                    link.link ===
                                                    location.pathname
                                                ) ||
                                                item.link === location.pathname
                                                  ? "active"
                                                  : ""
                                              } ${
                                                subsidebar === item.label
                                                  ? "subdrop"
                                                  : ""
                                              }`}
                                              onClick={() =>
                                                toggleSubsidebar(
                                                  item.label || ""
                                                )
                                              }
                                            >
                                              {item.label}
                                              {item.submenu && (
                                                <span className="menu-arrow" />
                                              )}
                                            </Link>

                                            {item.submenuItems &&
                                              subsidebar === item.label && (
                                                <ul
                                                  style={{ display: "block" }}
                                                >
                                                  {item.submenuItems
                                                    .filter(hasAccess)
                                                    .map(
                                                      (
                                                        subitem: SidebarItem,
                                                        k: number
                                                      ) => (
                                                        <li
                                                          className={
                                                            subitem.submenuItems
                                                              ? "submenu submenu-three"
                                                              : ""
                                                          }
                                                          key={`submenu-item-${k}`}
                                                        >
                                                          <Link
                                                            to={
                                                              subitem.submenu
                                                                ? "#"
                                                                : subitem.link ||
                                                                  "#"
                                                            }
                                                            state={{
                                                              employee:
                                                                employeeId,
                                                            }}
                                                            className={`${
                                                              subitem.submenuItems?.some(
                                                                (link: any) =>
                                                                  link.link ===
                                                                  location.pathname
                                                              ) ||
                                                              subitem.link ===
                                                                location.pathname
                                                                ? "active"
                                                                : ""
                                                            }`}
                                                          >
                                                            {subitem.label}
                                                            {subitem.submenu && (
                                                              <span className="menu-arrow" />
                                                            )}
                                                          </Link>

                                                          {subitem.submenuItems && (
                                                            <ul
                                                              style={{
                                                                display:
                                                                  "block",
                                                              }}
                                                            >
                                                              {subitem.submenuItems
                                                                .filter(
                                                                  hasAccess
                                                                )
                                                                .map(
                                                                  (
                                                                    deepSubItem: SidebarItem,
                                                                    d: number
                                                                  ) => (
                                                                    <li
                                                                      key={`deep-submenu-item-${d}`}
                                                                    >
                                                                      <Link
                                                                        to={
                                                                          deepSubItem.link ||
                                                                          "#"
                                                                        }
                                                                        state={{
                                                                          employee:
                                                                            employeeId,
                                                                        }}
                                                                        className={`${
                                                                          deepSubItem.link ===
                                                                          location.pathname
                                                                            ? "active"
                                                                            : ""
                                                                        }`}
                                                                      >
                                                                        {
                                                                          deepSubItem.label
                                                                        }
                                                                      </Link>
                                                                    </li>
                                                                  )
                                                                )}
                                                            </ul>
                                                          )}
                                                        </li>
                                                      )
                                                    )}
                                                </ul>
                                              )}
                                          </li>
                                        ))}
                                    </ul>
                                  )}
                              </li>
                            );
                          }
                        )}
                      </ul>
                    </li>
                  </React.Fragment>
                );
              })}
            </ul>
          </div>
        </div>
      </Scrollbars>
    </div>
  );
};

export default Sidebar;
